// =============================================================================
//  Klientská autentizace – vlastní JWT (nahrazuje Supabase Auth na frontendu).
//  Token se ukládá do localStorage; role a expirace se čtou přímo z JWT payloadu.
// =============================================================================

const TOKEN_KEY = 'chopsticks_token';

/** Dekóduje payload JWT bez ověření podpisu (podpis ověřuje server). */
function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/** Vrátí { id, email, role, exp } z tokenu nebo null. */
export function getUser() {
  const token = getToken();
  if (!token) return null;
  const payload = decodeToken(token);
  if (!payload) return null;
  return { id: payload.sub, email: payload.email, role: payload.role, exp: payload.exp };
}

/** True, pokud máme platný, neexpirovaný token. */
export function isAuthenticated() {
  const user = getUser();
  if (!user) return false;
  if (user.exp && Date.now() >= user.exp * 1000) {
    logout();
    return false;
  }
  return true;
}

/** Přihlášení; uloží token. Vyhodí Error se zprávou při neúspěchu. */
export async function login(email, password) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Zachováme skutečnou zprávu ze serveru (rate-limit / server error) i status,
    // aby ji přihlašovací obrazovka mohla rozlišit od chybných přihlašovacích údajů.
    const err = new Error(data.error || 'Přihlášení selhalo.');
    err.status = res.status;
    throw err;
  }
  localStorage.setItem(TOKEN_KEY, data.token);
  return data.user;
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Vyprší-li token (401), zahodíme ho a přesměrujeme na přihlášení – ať se
 * dashboard po 12 h nezasekne v „offline" zombie stavu. Chráníme před smyčkou:
 * na /login už znovu nepřesměrováváme.
 */
export function handleAuthExpired() {
  logout();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.replace('/login');
  }
}

/**
 * fetch s automatickým přidáním Authorization hlavičky.
 * Použití identické jako fetch(): authFetch(url, { method, body, headers }).
 * Na 401 (expirovaný/neplatný token) rovnou odhlásí a přesměruje na /login.
 */
export async function authFetch(url, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    handleAuthExpired();
  }
  return res;
}

/** Přihlášení kódem personálu (tablet). Uloží token stejně jako login(). */
export async function loginWithCode(code) {
  const res = await fetch('/api/auth/code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Přihlášení selhalo.');
    err.status = res.status;
    throw err;
  }
  localStorage.setItem(TOKEN_KEY, data.token);
  return data.user;
}
